import { useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import {
  DicountCodePageQuery,
  DicountCodePageQueryVariables,
  useGetLocationsQuery,
} from "src/generated/graphql";
import { GetDiscountCodePage } from "src/api/graphql/queries/Discount";
import { FetchParams, QueryResponse } from "@kapex/tables";
import { ArrayElement } from "src/utils/helperTypes";
import { useContext, useEffect } from "react";
import { AppContext } from "src/api/AppContext";
import { useNavigate } from "react-router-dom";
import {
  DEFAULT_ORDER,
  EMPLOYEE_ROLE_OBJ,
  ROUTES,
  ROUTE_NAMES,
} from "src/utils/constants";
import { Button, Text } from "@mantine/core";
import CreateDiscountForm from "./CreateForm";
import ScrollableDataGrid from "src/components/ScrollableDataGrid";
import { useDisclosure } from "@mantine/hooks";
import { useModals } from "@mantine/modals";
import useWritePermission from "src/hooks/usePermission";

export type DiscountData = ArrayElement<
  DicountCodePageQuery["dicountCodePage"]["items"]
>;

const DiscountsList = () => {
  const navigate = useNavigate();
  const modals = useModals();
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.orgDiscounts);
  const [allowRefetch, { toggle: setAllowRefetch }] = useDisclosure(false);
  const { setCurrentPageTitle } = useContext(AppContext);
  const { data: locationsArray } = useGetLocationsQuery();
  const { query } = useApolloClient();

  const getDiscounts = async (
    fetchParams: FetchParams
  ): Promise<QueryResponse<DiscountData>> => {
    let { data } = await query<
      DicountCodePageQuery,
      DicountCodePageQueryVariables
    >({
      query: GetDiscountCodePage,
      variables: {
        pagination: {
          count: fetchParams.count,
          page: fetchParams.page,
          orderBy: fetchParams.orderBy || DEFAULT_ORDER.name,
          filters: fetchParams.filters,
        },
      },
    });

    if (data.dicountCodePage) {
      return {
        rowCount: data.dicountCodePage.totalRows,
        rowData: data.dicountCodePage.items as DiscountData[],
      };
    }
    return {
      rowData: [],
      rowCount: 0,
    };
  };

  useEffect(() => {
    setCurrentPageTitle(ROUTE_NAMES.DISCOUNTS);

    return () => {
      setCurrentPageTitle(undefined);
    };
  }, []);

  const handleNavigation = (id: string): void => {
    navigate(`${ROUTES.DISCOUNTS}/${id}`);
  };

  const openDiscountModal = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Create Discount Code",
      centered: true,
      children: (
        <CreateDiscountForm
          close={modals.closeAll}
          onRefetch={setAllowRefetch}
          locationsList={locationsArray?.locationMany?.map((val) => val)}
        />
      ),
    });
  }, [locationsArray?.locationMany, modals, setAllowRefetch]);

  const TableGrid = useCallback(
    () => (
      <ScrollableDataGrid<DiscountData>
        topMargin={56}
        columnConfigs={[
          {
            field: "name",
            name: "Name",
            filter: {
              field: "name",
              type: "text",
            },
            sortable: true,
            cellRenderer: (rowItem) => (
              <Text
                weight={500}
                onClick={() => handleNavigation(rowItem.id)}
                style={{ cursor: "pointer" }}
              >
                {rowItem.name}
              </Text>
            ),
          },
          {
            field: "value",
            name: "Value",
            filter: {
              field: "value",
              type: "text",
            },
            sortable: true,
            cellRenderer: (rowItem) =>
              rowItem.isPercent ? (
                <Text>{rowItem.value}%</Text>
              ) : (
                <Text>$ {rowItem.value}</Text>
              ),
          },
          {
            field: "createdDate",
            name: "Created",
            filter: {
              field: "createdDate",
              type: "dateRange",
            },
            sortable: true,
            valueParser: (rowItem) => {
              return new Date(rowItem.createdDate).toDateString();
            },
          },
        ]}
        dataFetch={getDiscounts}
      />
    ),
    [allowRefetch]
  );

  return (
    <>
      {isAllowWrite && (
        <Button mb={20} onClick={openDiscountModal}>
          Create +
        </Button>
      )}
      <TableGrid />
    </>
  );
};

export default DiscountsList;
